import { useState, useEffect } from "react";
import { X, Download, FileText, Printer } from "lucide-react";

const PRESETS = [
    { v: "3bln", l: "3 Bulan Terakhir", days: 90 },
    { v: "6bln", l: "6 Bulan Terakhir", days: 180 },
    { v: "1thn", l: "1 Tahun Terakhir", days: 365 },
    { v: "custom", l: "Pilih Tanggal Custom", days: 0 },
];

function toISO(d) { return d.toISOString().slice(0, 10); }

export function DateRangeModal({ open, mode, onClose, onConfirm }) {
    const [preset, setPreset] = useState("3bln");
    const [dari, setDari] = useState("");
    const [sampai, setSampai] = useState("");

    useEffect(() => {
        if (!open) return;
        const today = new Date();
        setSampai(toISO(today));
        setPreset("3bln");
        const d = new Date(today);
        d.setDate(d.getDate() - 90);
        setDari(toISO(d));
    }, [open]);

    useEffect(() => {
        if (preset === "custom") return;
        const p = PRESETS.find((x) => x.v === preset);
        if (!p) return;
        const today = new Date();
        setSampai(toISO(today));
        const d = new Date(today);
        d.setDate(d.getDate() - p.days);
        setDari(toISO(d));
    }, [preset]);

    if (!open) return null;

    const isExcel = mode === "excel";
    const title = isExcel ? "Export Excel — Pilih Periode" : "Cetak Buku Vendor — Pilih Periode";
    const btnLabel = isExcel ? "Export Excel" : "Buka Preview PDF";
    const Icon = isExcel ? Download : Printer;

    const invalid = !dari || !sampai || dari > sampai;

    const confirm = () => {
        if (invalid) return;
        onConfirm({ dari, sampai });
    };

    return (
        <div
            className="fixed inset-0 bg-slate-900/40 z-50 grid place-items-center p-4"
            onClick={onClose}
            data-testid="date-range-modal"
        >
            <div
                className="panel p-5 w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-slate-900 flex items-center gap-2">
                        {isExcel ? <FileText className="w-4 h-4 text-teal-600" /> : <Printer className="w-4 h-4 text-teal-600" />}
                        {title}
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="date-range-close">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="space-y-2 mb-4">
                    {PRESETS.map((p) => (
                        <label
                            key={p.v}
                            className={`flex items-center gap-3 p-2.5 rounded border cursor-pointer transition-colors ${
                                preset === p.v
                                    ? "border-teal-500 bg-teal-50"
                                    : "border-[var(--border)] hover:bg-slate-50"
                            }`}
                            data-testid={`preset-${p.v}`}
                        >
                            <input
                                type="radio"
                                name="preset"
                                value={p.v}
                                checked={preset === p.v}
                                onChange={() => setPreset(p.v)}
                                className="accent-teal-600"
                            />
                            <span className="text-sm font-medium">{p.l}</span>
                        </label>
                    ))}
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                    <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Dari</label>
                        <input
                            type="date"
                            value={dari}
                            onChange={(e) => { setDari(e.target.value); setPreset("custom"); }}
                            disabled={preset !== "custom"}
                            className="w-full border border-[var(--border)] rounded px-2 py-1.5 text-sm disabled:bg-slate-50 disabled:text-slate-600"
                            data-testid="date-range-dari"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Sampai</label>
                        <input
                            type="date"
                            value={sampai}
                            onChange={(e) => { setSampai(e.target.value); setPreset("custom"); }}
                            disabled={preset !== "custom"}
                            className="w-full border border-[var(--border)] rounded px-2 py-1.5 text-sm disabled:bg-slate-50 disabled:text-slate-600"
                            data-testid="date-range-sampai"
                        />
                    </div>
                </div>

                {invalid && (
                    <div className="text-xs text-red-600 mb-3">
                        Rentang tanggal tidak valid. Pastikan "Dari" tidak melewati "Sampai".
                    </div>
                )}

                <div className="flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="px-3 py-1.5 border border-slate-300 rounded text-sm hover:bg-slate-50"
                        data-testid="date-range-cancel"
                    >
                        Batal
                    </button>
                    <button
                        onClick={confirm}
                        disabled={invalid}
                        className="px-3 py-1.5 bg-teal-600 hover:bg-teal-700 disabled:bg-slate-300 text-white rounded text-sm font-medium inline-flex items-center gap-1.5"
                        data-testid="date-range-confirm"
                    >
                        <Icon className="w-3.5 h-3.5" /> {btnLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}
